/**
 * Enum matching backend NotificationTypes
 */
export enum NotificationType {
  EventCreated = 'EventCreated',
  EventCompleted = 'EventCompleted',
  GameCreated = 'GameCreated',
  GamePublished = 'GamePublished',
  GameSubmitted = 'GameSubmitted',
  GameApproved = 'GameApproved',
  GameDuplicated = 'GameDuplicated',
  AssessmentCreated = 'AssessmentCreated',
  AssessmentPublished = 'AssessmentPublished',
  AssessmentSubmitted = 'AssessmentSubmitted',
  AssessmentApproved = 'AssessmentApproved',
  AssessmentDuplicated = 'AssessmentDuplicated',
  FeedbackCreated = 'FeedbackCreated',
  FeedbackPublished = 'FeedbackPublished',
  FeedbackSubmitted = 'FeedbackSubmitted',
  FeedbackApproved = 'FeedbackApproved',
  FeedbackDuplicated = 'FeedbackDuplicated',
  GlobalLibraryGameAdded = 'GlobalLibraryGameAdded',
  GlobalLibraryAssessmentAdded = 'GlobalLibraryAssessmentAdded',
  GlobalLibraryFeedbackAdded = 'GlobalLibraryFeedbackAdded'
}

/**
 * Human-readable messages for each notification type.
 * Use {metadata} as placeholder for the notification metadata (e.g., game name).
 */
export const NotificationTypeMessages: Record<string, string> = {
  [NotificationType.EventCreated]: 'created a new event',
  [NotificationType.EventCompleted]: 'has been completed',
  [NotificationType.GameCreated]: 'created a new game',
  [NotificationType.GamePublished]: 'published the game',
  [NotificationType.GameSubmitted]: 'submitted the game for review',
  [NotificationType.GameApproved]: 'approved the game',
  [NotificationType.GameDuplicated]: 'duplicated the game',
  [NotificationType.AssessmentCreated]: 'created a new assessment',
  [NotificationType.AssessmentPublished]: 'published the assessment',
  [NotificationType.AssessmentSubmitted]: 'submitted the assessment for review',
  [NotificationType.AssessmentApproved]: 'approved the assessment',
  [NotificationType.AssessmentDuplicated]: 'duplicated the assessment',
  [NotificationType.FeedbackCreated]: 'created a new feedback',
  [NotificationType.FeedbackPublished]: 'published the feedback',
  [NotificationType.FeedbackSubmitted]: 'submitted the feedback for review',
  [NotificationType.FeedbackApproved]: 'approved the feedback',
  [NotificationType.FeedbackDuplicated]: 'duplicated the feedback',
  [NotificationType.GlobalLibraryGameAdded]:
    'added a game to the Global Library',
  [NotificationType.GlobalLibraryAssessmentAdded]:
    'added an assessment to the Global Library',
  [NotificationType.GlobalLibraryFeedbackAdded]:
    'added feedback to the Global Library'
};

/**
 * Messages for rejected notifications (when metadata.Rejected is true)
 */
export const NotificationTypeRejectedMessages: Record<string, string> = {
  [NotificationType.GameApproved]: 'rejected the game',
  [NotificationType.AssessmentApproved]: 'rejected the assessment',
  [NotificationType.FeedbackApproved]: 'rejected the feedback'
};

/**
 * Parse metadata and check if notification is rejected.
 */
const isRejected = (metadata?: string): boolean => {
  if (!metadata) return false;
  try {
    const parsed = JSON.parse(metadata);
    return parsed.rejected === true;
  } catch {
    return false;
  }
};

/**
 * Get human-readable message for a notification type.
 * Falls back to the raw type if no mapping exists.
 * @param type - The notification type
 * @param metadata - Optional JSON metadata string, may contain Rejected field
 */
export const getNotificationMessage = (
  type?: string,
  metadata?: string
): string => {
  if (!type) return '';

  // Check if this is a rejected notification
  if (isRejected(metadata) && NotificationTypeRejectedMessages[type]) {
    return NotificationTypeRejectedMessages[type];
  }

  return NotificationTypeMessages[type] ?? type;
};
